/**
 * Store + helpers impératifs pour afficher des dialogs Alert/Confirm/Prompt
 * propres, en remplacement des `alert/confirm/prompt` natifs du browser
 * (qui sont moches, inconsistants entre OS, et iOS Safari mute parfois).
 *
 * Architecture :
 *  - 1 store réactif `dialogState` qui contient le dialog courant ou null.
 *  - 3 helpers `alertDialog`, `confirmDialog`, `promptDialog` qui posent
 *    le dialog dans le store et renvoient une Promise résolue à la
 *    fermeture (par l'user).
 *  - 1 composant `DialogHost.svelte` (monté une fois dans App.svelte)
 *    lit le store et rend le dialog actif.
 *
 * Stratégie 1 dialog à la fois : si on appelle un helper alors qu'un
 * dialog est déjà ouvert, on ferme le précédent (resolve avec la valeur
 * "annulé" = false/null) puis on ouvre le nouveau. Suffisant pour notre
 * usage actuel. Une queue serait possible si besoin.
 *
 * Exemples :
 *   const ok = await confirmDialog({
 *     title: 'Supprimer ?',
 *     message: 'Cette action est irréversible.',
 *     variant: 'danger',
 *     confirmLabel: 'Supprimer',
 *   });
 *   if (!ok) return;
 *
 *   const name = await promptDialog({
 *     title: 'Nouveau nom',
 *     defaultValue: 'Patron',
 *   });
 *   if (name === null) return;
 */

export type DialogVariant = 'default' | 'danger';

export type AlertOptions = {
  title: string;
  message: string;
  /** Label du bouton OK. Défaut : "OK". */
  okLabel?: string;
};

export type ConfirmOptions = {
  title: string;
  message: string;
  /** Label du bouton de confirmation. Défaut : "Confirmer". */
  confirmLabel?: string;
  /** Label du bouton d'annulation. Défaut : "Annuler". */
  cancelLabel?: string;
  /** "danger" rend le bouton de confirmation rouge — pour des actions
   *  destructives (suppression, dépublication…). */
  variant?: DialogVariant;
};

export type PromptOptions = {
  title: string;
  /** Petit texte explicatif au-dessus de l'input. */
  message?: string;
  /** Valeur initiale de l'input. */
  defaultValue?: string;
  /** Placeholder visible quand vide. */
  placeholder?: string;
  /** Label OK. Défaut : "Valider". */
  okLabel?: string;
  /** Label Annuler. Défaut : "Annuler". */
  cancelLabel?: string;
  /** Validation custom — retourne une string d'erreur ou null si valide.
   *  Empêche la fermeture par OK tant que l'erreur n'est pas vide. */
  validate?: (value: string) => string | null;
};

/** State interne du dialog affiché par le DialogHost. */
export type DialogState =
  | { type: 'alert'; opts: AlertOptions; resolve: () => void }
  | { type: 'confirm'; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { type: 'prompt'; opts: PromptOptions; resolve: (v: string | null) => void }
  | null;

/**
 * Singleton réactif consommé par DialogHost.svelte. On utilise un objet
 * `$state` simple pour pouvoir l'importer depuis n'importe où.
 */
export const dialogStore: { current: DialogState } = $state({ current: null });

function closeAndResolve(value: false | null | undefined): void {
  const s = dialogStore.current;
  if (!s) return;
  dialogStore.current = null;
  // Resolve avec la valeur "annulé" selon le type
  if (s.type === 'alert') s.resolve();
  else if (s.type === 'confirm') s.resolve(false);
  else s.resolve(null);
  void value; // shut up TS si pas utilisé
}

/**
 * Pose un nouveau dialog dans le store. Si un dialog est déjà ouvert,
 * il est fermé (resolve avec valeur "annulé") avant que le nouveau
 * s'affiche. Garantit qu'on n'a qu'un dialog à la fois.
 */
function pushDialog(next: NonNullable<DialogState>): void {
  if (dialogStore.current) closeAndResolve(undefined);
  dialogStore.current = next;
}

// ---------- Helpers publics ----------

export function alertDialog(opts: AlertOptions): Promise<void> {
  return new Promise<void>((resolve) => {
    pushDialog({ type: 'alert', opts, resolve });
  });
}

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    pushDialog({ type: 'confirm', opts, resolve });
  });
}

export function promptDialog(opts: PromptOptions): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    pushDialog({ type: 'prompt', opts, resolve });
  });
}

// ---------- API interne consommée par DialogHost ----------

/** Résoud le dialog courant avec une valeur explicite (depuis le host). */
export function resolveCurrent(value?: boolean | string | null): void {
  const s = dialogStore.current;
  if (!s) return;
  dialogStore.current = null;
  if (s.type === 'alert') (s.resolve as () => void)();
  else if (s.type === 'confirm') (s.resolve as (v: boolean) => void)(value as boolean);
  else (s.resolve as (v: string | null) => void)(value as string | null);
}

/** Ferme le dialog courant sans valeur (= annulé). */
export function cancelCurrent(): void {
  closeAndResolve(undefined);
}
